import { RJSFSchema, UiSchema } from '@rjsf/utils';
import { OutsideInventorySchema } from '../../../types';

type SchemaPair = OutsideInventorySchema['schema'];
type PropertyDefinition = RJSFSchema['properties'] extends infer P ? P[keyof P] : never;
type RevealedField = { name: string; title: string };

const SHOW_ALL = 'showAll';
// Schemas saved before the API stopped listing these still name them; a text input can't hold any of them safely.
const NEVER_REVEALED = new Set(['phoneNumbers', 'locationContact', 'speed', 'portSpeed', 'accessSpeed', 'bandwidth']);

const revealedFields = (uiSchema: UiSchema): RevealedField[] =>
  (uiSchema[SHOW_ALL]?.['ui:options']?.fields || []).filter(({ name }: RevealedField) => !NEVER_REVEALED.has(name));

const without = <T>(record: Record<string, T>, key: string): Record<string, T> =>
  Object.fromEntries(Object.entries(record).filter(([name]) => name !== key));

const fallbackDefinition = (value: unknown): PropertyDefinition => ({
  type: [typeof value === 'number' ? 'number' : 'string', 'null']
});

const branchesOf = (schema: RJSFSchema, dependency: string): RJSFSchema[] => {
  const branches = (schema.dependencies?.[dependency] as RJSFSchema | undefined)?.oneOf;
  return (branches as RJSFSchema[]) || [];
};

const dependencyNames = (schema: RJSFSchema) =>
  Object.keys(schema.dependencies || {}).filter((name) => name !== SHOW_ALL);

const definedProperties = (schema: RJSFSchema): Record<string, PropertyDefinition> =>
  Object.assign(
    {},
    ...dependencyNames(schema).flatMap((name) => branchesOf(schema, name).map((branch) => branch.properties || {})),
    schema.properties || {}
  );

const advancedProperties = (schema: RJSFSchema): Record<string, PropertyDefinition> => {
  const branchProperties = branchesOf(schema, 'showAdvanced').map((branch) => branch.properties || {});
  return without(Object.assign({}, ...branchProperties), 'showAdvanced');
};

const definitionFor = (field: string, record: RJSFSchema, allSchemas: OutsideInventorySchema[], value: unknown) =>
  [record, ...allSchemas.map((s) => s.schema.validation_schema)]
    .map((schema) => definedProperties(schema)[field])
    .find(Boolean) || fallbackDefinition(value);

const typesOf = (definition: PropertyDefinition): unknown[] =>
  typeof definition === 'object' ? ([] as unknown[]).concat(definition.type ?? []) : [];

const holdsScalar = (definition: PropertyDefinition, value: unknown) =>
  (value === null || typeof value !== 'object') &&
  !typesOf(definition).some((type) => type === 'array' || type === 'object');

const holdsBooleanOnly = (definition: PropertyDefinition) => {
  const types = typesOf(definition).filter((type) => type !== 'null');
  return types.length > 0 && types.every((type) => type === 'boolean');
};

const leniently = (definition: PropertyDefinition, value: unknown): PropertyDefinition => {
  if (typeof definition !== 'object') return definition;
  const declaredTypes = typesOf(definition).length ? typesOf(definition) : typesOf(fallbackDefinition(value));
  const storedType = value === null || value === undefined ? [] : [typeof value];
  const lenient: RJSFSchema = {
    type: [...new Set([...declaredTypes, ...storedType, 'null'])] as RJSFSchema['type']
  };
  if (definition.title) lenient.title = definition.title;
  if (definition.enum) lenient.enum = [...new Set([...definition.enum, null, ...(value === undefined ? [] : [value])])];
  return lenient;
};

const widgetFor = (field: string, definition: PropertyDefinition): string => {
  if (typeof definition !== 'object') return 'TextInput';
  if (definition.enum) return 'SelectInput';
  if (field === 'nRC') return 'CurrencyInput';
  const types = typesOf(definition);
  if (types.includes('number') || types.includes('integer')) return 'IntegerInput';
  if (/date$/i.test(field)) return 'DateInput';
  return 'TextInput';
};

const SENT_TYPES: Record<string, string[]> = {
  IntegerInput: ['number'],
  CurrencyInput: ['number', 'string'],
  DateInput: ['string', 'null']
};

const acceptingInput = (definition: PropertyDefinition, widget: string): PropertyDefinition => {
  if (typeof definition !== 'object' || !typesOf(definition).length) return definition;
  const sent = SENT_TYPES[widget] || ['string'];
  return { ...definition, type: [...new Set([...typesOf(definition), ...sent])] as RJSFSchema['type'] };
};

export const withoutShowAllBranch = (schema: RJSFSchema): RJSFSchema => ({
  ...schema,
  dependencies: without(schema.dependencies || {}, SHOW_ALL)
});

export const revealHiddenFields = (
  schema: SchemaPair,
  allSchemas: OutsideInventorySchema[],
  values: Record<string, unknown>
): SchemaPair => {
  const validation_schema = withoutShowAllBranch(schema.validation_schema);
  const properties = { ...advancedProperties(validation_schema), ...validation_schema.properties };
  const ui_schema: UiSchema = { ...schema.ui_schema };

  revealedFields(schema.ui_schema).forEach(({ name, title }) => {
    const definition = definitionFor(name, validation_schema, allSchemas, values[name]);
    if (!holdsScalar(definition, values[name]) || holdsBooleanOnly(definition)) return;
    const widget = widgetFor(name, definition);
    properties[name] = properties[name] || acceptingInput(leniently(definition, values[name]), widget);
    ui_schema[name] = {
      ...ui_schema[name],
      'ui:widget': widget,
      'ui:label': false,
      'ui:hideError': true,
      'ui:placeholder': title
    };
  });

  return { validation_schema: { ...validation_schema, properties }, ui_schema };
};
